import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useToast } from '../stores/toast.js';
import { useDialogueDuDessus } from '../ui/Dialogue.js';

// Le temps qu'un lecteur d'écran enregistre une région live qui vient d'être insérée.
const DELAI_REGION_LIVE_MS = 100;

// `.notifications` du kit : le conteneur (une région `aria-live`) est toujours là, vide au
// repos — c'est ce qui fait qu'un message qui y arrive est annoncé.
//
// Il se rend DANS le dialogue du dessus quand il y en a un : un <dialog> modal rend le reste de
// la page inerte et la couvre de son voile, et « Undo » y serait visible mais incliquable
// (cocher une tâche, puis en ouvrir une autre dans les cinq secondes).
//
// Changer la cible d'un portail recrée le conteneur : inséré déjà rempli, il ne serait pas
// annoncé (« Created · … » de QuickAdd, qui ferme son dialogue et notifie dans le même tick).
// Le message n'entre donc dans un nouveau conteneur qu'après un court délai, conteneur vide.
export function Toast(): React.ReactElement {
  const toast = useToast((s) => s.toast);
  const runAction = useToast((s) => s.runAction);
  const dismiss = useToast((s) => s.dismiss);
  const dialogue = useDialogueDuDessus();
  const cible = dialogue ?? document.body;
  const [cibleAnnoncable, setCibleAnnoncable] = useState<Element | null>(null);

  useEffect(() => {
    const minuterie = setTimeout(() => setCibleAnnoncable(cible), DELAI_REGION_LIVE_MS);
    return () => clearTimeout(minuterie);
  }, [cible]);

  return createPortal(
    <div className="notifications" aria-live="polite">
      {toast && cibleAnnoncable === cible && (
        <div className="notification" role="status">
          <span>{toast.message}</span>
          {toast.action && (
            <button
              type="button"
              className="bouton bouton-fantome bouton-petit"
              onClick={() => void runAction()}
            >
              {toast.action.label}
            </button>
          )}
          <button
            type="button"
            className="bouton bouton-discret bouton-icone bouton-petit"
            onClick={dismiss}
            aria-label="Dismiss"
          >
            <X size={16} aria-hidden />
          </button>
        </div>
      )}
    </div>,
    cible
  );
}
